import React, { useEffect, useMemo, useRef, useState } from "react"

import { DatePicker, Dropdown, Flex, Typography } from "antd"
import cls from "classname"
import dayjs from "dayjs"
import quarterOfYear from "dayjs/plugin/quarterOfYear"
import weekday from "dayjs/plugin/weekday"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Icon from "../../../Icon"
import { calendarLocale } from "../../../Utils/utils"
import ButtonComponent from "../../Button"
import SelectComponent from "../../Select"
import { findPresetLabel, formatRangeLabel, RANGE_PRESETS } from "./periodLabel"

import "./DropdownDateBlock.less"

dayjs.extend(weekday)
dayjs.extend(quarterOfYear)

const isoFormat = "YYYY-MM-DD"

const { RangePicker } = DatePicker
const { Text } = Typography

// Index where the rolling ranges end and the "to date" ranges begin.
const PRESET_DIVIDER_INDEX = 5

export default function DropdownDateBlock({
  label,
  iconType,
  id,
  customOverlayClassName,
  wrapperClassName,
  dropdownFilters,
  setDropdownFilters,
  disabled,
  inactive,
  onOpenChange,
  alwaysShowLabel = true,
  presetId: presetIdProp = null
}) {
  //inner dropdown period ref
  const dropdownInner = useRef()
  //calendar column ref (so the RangePicker panel renders beside the preset rail)
  const calendarRef = useRef()
  const { t } = useTranslation()
  const [clickSame, setClickSame] = useState(null)

  //show/hide outer dropdown
  const [visible, setVisible] = useState(false)

  //dayjs date value
  const [dateValue, setDateValue] = useState(null)
  const [isApply, setIsApply] = useState(false)
  const [selectedPresetId, setSelectedPresetId] = useState(null)

  const lang = useSelector((store) => store?.auth?.userInfo?.language) || "en"
  const isMobile = useSelector((state) => state.nav.isMobile)
  const isDisabledTransition = useSelector((state) => state.nav.disabledTransition)

  //calendar display settings
  dayjs.updateLocale("en-us", { week: { dow: 1 } })

  useEffect(() => {
    setInitialValue()
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [dropdownFilters, presetIdProp])

  const setInitialValue = () => {
    if (typeof dropdownFilters === "string") {
      setDateValue(null)
      setSelectedPresetId(dropdownFilters)
      return
    }
    const [min = null, max = null] = dropdownFilters || []
    setSelectedPresetId(presetIdProp)
    if (!min && !max) {
      setDateValue(null)
    } else {
      setDateValue({
        min: min ? dayjs(min).format("YYYY-MM-DD") : null,
        max: max ? dayjs(max).format("YYYY-MM-DD") : null
      })
    }
  }

  // Which preset (if any) matches the currently-selected range — used to
  // highlight the active item in the rail.
  const activePreset = useMemo(
    () =>
      findPresetLabel({ min: dateValue?.min, max: dateValue?.max, presetId: selectedPresetId }),
    [dateValue, selectedPresetId]
  )

  //toggle outer dropdown show/hide
  const handleVisibleChange = (value) => {
    if (value) setIsApply(false)
    if (!value && !isApply) handleCancel()
    setVisible(value)
    onOpenChange?.(value)
  }

  //string to display in outer dropdown after change is made — show the matching
  //preset name (e.g. "Last 4 weeks"), otherwise the formatted range.
  function getSelectValue() {
    if (activePreset) {
      return alwaysShowLabel ? `${label || t("Date")}: ${t(activePreset)}` : t(activePreset)
    }
    let result = null
    if (!dateValue?.min && !dateValue?.max) {
    } else {
      result = formatRangeLabel(dateValue?.min, dateValue?.max, lang)
    }

    return result ? `${label || t("Date")}: ${result}` : label || t("Custom")
  }

  function handleCancel() {
    setTimeout(() => setInitialValue(), 0)
    if (visible) setVisible(false)
  }

  const handleApply = () => {
    setIsApply(true)
    if (dateValue?.min && dateValue?.max) {
      setDropdownFilters({
        [id]: [dateValue.min, dateValue.max],
        [`__preset_${id}`]: selectedPresetId ?? null
      })
    } else {
      setDropdownFilters({ [id]: null })
    }
    setVisible(false)
    onOpenChange?.(false)
  }

  // Apply a quick-range preset immediately and close.
  const handlePreset = (preset) => {
    const [from, to] = preset.range()
    const min = from.format(isoFormat)
    const max = to.format(isoFormat)
    setSelectedPresetId(preset.id)
    setDateValue({ min, max })
    setIsApply(true)
    setDropdownFilters({
      [id]: preset.value ?? [min, max],
      [`__preset_${id}`]: preset.id
    })
    setVisible(false)
  }

  const cellRender = (current, today) => {
    const currentStr = current.format("YYYY-MM-DD")
    const handleClick = () => {
      if (currentStr === dateValue?.min || currentStr === dateValue?.max) {
        setClickSame(currentStr)
        setTimeout(() => setClickSame(null), 400)
      } else setClickSame(null)
    }

    return (
      <div
        onClick={handleClick}
        className={cls("ant-picker-cell-inner", {
          //style days after today
          "ant-picker-future": dayjs().endOf("day") < current,
          "ant-picker-cell-already-selected": clickSame === currentStr,
          "ant-picker-cell-selected": currentStr === dateValue?.min || currentStr === dateValue?.max
        })}
      >
        {current.date()}
      </div>
    )
  }

  return (
    <div
      className={cls("select-block select-date-block", wrapperClassName, {
        "disabled-transition": isDisabledTransition
      })}
    >
      <Dropdown
        overlayClassName={customOverlayClassName || "dropdown-database-wrapper"}
        className={customOverlayClassName}
        destroyPopupOnHide={true}
        getPopupContainer={() => document.getElementById("main-layout")}
        onOpenChange={handleVisibleChange}
        dropdownRender={() => (
          <div ref={dropdownInner} className={"dropdown-date-wrapper trial-range-wrapper"}>
            {isMobile ? (
              <SelectComponent
                style={{ margin: "16px 16px 0 16px" }}
                placeholder={t("Select Date")}
                value={activePreset || null}
                options={RANGE_PRESETS.map((p) => ({
                  label: t(p.label),
                  value: p.label
                }))}
                onChange={(value) => {
                  const preset = RANGE_PRESETS.find((p) => p.label === value)
                  if (preset) handlePreset(preset)
                }}
              />
            ) : (
              <div className="trial-range-presets">
                {RANGE_PRESETS.map((p, i) => (
                  <React.Fragment key={p.label}>
                    {i === PRESET_DIVIDER_INDEX && <div className="trial-range-divider" />}
                    <button
                      type="button"
                      className={cls("trial-range-preset", {
                        active: activePreset === p.label
                      })}
                      onClick={() => handlePreset(p)}
                    >
                      {t(p.label)}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            <div className="trial-range-main" ref={calendarRef}>
              <RangePicker
                className={"dropdown-date-rangepicker"}
                value={[
                  dateValue?.min ? dayjs(dateValue?.min) : null,
                  dateValue?.max ? dayjs(dateValue?.max) : null
                ]}
                open={true}
                format={"YYYY-MM-DD"}
                locale={calendarLocale(lang)}
                nextIcon={<Icon type={"chevron_right_small"} style={{ fontSize: 24 }} />}
                prevIcon={<Icon type={"chevron_left_small"} style={{ fontSize: 24 }} />}
                allowClear={<Icon type="close_small" />}
                onCalendarChange={(date, strings, info) => {
                  setSelectedPresetId(null)
                  if (date.filter((el) => el !== null).length) {
                    const inputs = document.querySelectorAll(".ant-picker-input")
                    //if date selected, set local and parent states
                    let min =
                      info?.range === "end" && !date?.[0]
                        ? date[1]?.format("YYYY-MM-DD")
                        : date?.[0]?.format("YYYY-MM-DD")
                    let max =
                      info?.range === "start"
                        ? date?.[0]?.format("YYYY-MM-DD")
                        : date[1]?.format("YYYY-MM-DD")
                    setDateValue({ min: min, max: max })
                    //setDateValue({ min: date[0]?.format('YYYY-MM-DD'), max: date[1]?.format('YYYY-MM-DD') });

                    if (info.range === "start") {
                      return inputs?.[1]?.firstElementChild?.focus()
                    } else if (info.range === "end") {
                      if (strings?.[0] === strings?.[1])
                        return inputs?.[0]?.firstElementChild?.focus()
                      //if(strings[0] && strings[0] !== strings[1]) return inputs?.[1]?.firstElementChild?.blur()
                    }
                  } else {
                    setDateValue(null)
                  }
                }}
                getPopupContainer={() => calendarRef.current}
                placeholder={[t("Select Start Date"), t("Select End Date")]}
                cellRender={cellRender}
                //needConfirm={true}
                disabledDate={
                  ((current) => dayjs().endOf("day") < current) ||
                  (dateValue?.min &&
                    dateValue?.min === dateValue?.max &&
                    current < dayjs(dateValue?.min).endOf("day"))
                }
              />
              <Flex gap={12} align="center" className="dropdown-date-button-wrapper">
                <Text type="secondary" className="dropdown-date-timezone">
                  {t("Dates are in UTC")}
                </Text>
                <ButtonComponent
                  className={"flex center dropdown-date-button"}
                  text={t("Cancel")}
                  onClick={handleCancel}
                />
                <ButtonComponent
                  type="primary"
                  className={"flex center dropdown-date-button apply"}
                  text={t("Apply")}
                  onClick={handleApply}
                  style={{ height: 30 }}
                />
              </Flex>
            </div>
          </div>
        )}
        trigger={["click"]}
        open={visible}
        placement={isMobile ? "bottom" : "bottomLeft"}
        disabled={disabled}
      >
        <div
          className={cls(
            "dropdown-database",
            {
              "ant-dropdown-open": visible,
              "ant-dropdown-active": dropdownFilters?.[0] && !inactive
            },
            id
          )}
        >
          <Icon role="icon" type={iconType} />
          <Text className="dropdown-database_value">{getSelectValue()}</Text>
          <Icon type="chevron_down_small" className="arrow-icon" />
        </div>
      </Dropdown>
    </div>
  )
}
